import React, { useMemo } from 'react';
import Select from 'react-select';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export type ISelectBoxProps = {
	name: string;
	dataSource: any;
	value: any;
	onValueChanged: (value: any) => void;
	valueExpr: string;
	displayExpr: string;
	displaySubExpr?: string;
	label?: string;
	error?: string;
	sizeClass?: 'xs' | 'sm' | 'md' | 'lg';
	helpBlock?: string;
	placeholder?: string;
	labelSmall?: boolean;
	isDisabled?: boolean;
	isClearable?: boolean;
	isSearchable?: boolean;
	hasValid?: boolean;
	width?: any;
	grouped?: boolean;
	defaultText?: string;
};

export const SelectBox = (props: ISelectBoxProps) => {
	const {
		value,
		error,
		name,
		label,
		sizeClass,
		isDisabled,
		helpBlock,
		labelSmall,
		hasValid,
		dataSource,
		onValueChanged,
		valueExpr,
		displayExpr,
		placeholder,
		isClearable = true,
		isSearchable = true,
	} = props;
	const showError = !!error;

	const normalizedDataSource = useMemo(() => {
		if (!dataSource) return [];
		if (Array.isArray(dataSource)) {
			return dataSource.map((item: any) => ({
				value: item[valueExpr || 'id'],
				label: item[displayExpr || 'name'],
				...item,
			}));
		}
		return Object.values(dataSource).map((item: any) => ({
			value: item[valueExpr || 'id'],
			label: item[displayExpr || 'name'],
			...item,
		}));
	}, [dataSource, valueExpr, displayExpr]);

	const selectedOption = useMemo(() => {
		return normalizedDataSource.find(opt => opt.value === value) || null;
	}, [value, normalizedDataSource]);

	return (
		<div className="space-y-2">
			{label && (
				<Label htmlFor={name} className={cn(labelSmall && "text-xs")}>
					{label} {hasValid && <span className='text-destructive'>(*)</span>}
				</Label>
			)}
			<div className="relative">
				<Select
					inputId={name}
					isDisabled={isDisabled}
					isClearable={isClearable}
					isSearchable={isSearchable}
					placeholder={placeholder || 'Chọn...'}
					value={selectedOption}
					options={normalizedDataSource}
					onChange={(selected: any) => onValueChanged(selected ? selected.value : null)}
					classNamePrefix="react-select"
					styles={{
						control: (base, state) => ({
							...base,
							minHeight: sizeClass === 'sm' || sizeClass === 'xs' ? '32px' : '40px',
							borderRadius: '0.5rem',
							borderColor: showError ? 'hsl(var(--destructive))' : state.isFocused ? 'hsl(var(--ring))' : 'hsl(var(--input))',
							boxShadow: state.isFocused && showError 
								? '0 0 0 1px hsl(var(--destructive))' 
								: state.isFocused 
								? '0 0 0 1px hsl(var(--ring))' 
								: 'none',
							'&:hover': {
								borderColor: showError ? 'hsl(var(--destructive))' : 'hsl(var(--input))',
							}
						}),
						menu: (base) => ({
							...base,
							borderRadius: '0.5rem',
							overflow: 'hidden',
							zIndex: 50,
						}),
					}}
				/>
			</div>
			{showError && <p className="text-sm font-medium text-destructive">{error}</p>}
			{helpBlock && <p className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: helpBlock }} />}
		</div>
	);
};
